import React, { useEffect, useMemo, useState } from "react";
import { Card, Empty, Field } from "../../dashboard/primitives";
import { subscribeAllUsers } from "../../projects/api";
import {
  createOverageInvoice,
  getClientOverage,
  getClientUsage,
  setClientAllowance,
} from "../../lib/metering";

const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 0 })}`;

const Meter = ({ row }) => {
  const pct = row.included > 0 ? Math.min(100, (row.used / row.included) * 100) : row.used > 0 ? 100 : 0;
  const over = row.overage > 0;
  return (
    <div style={{ padding: "12px 0", borderBottom: "1px solid var(--fw-line)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 6 }}>
        <span style={{ fontWeight: 600 }}>{row.label}</span>
        <span style={{ fontFamily: "var(--fw-mono)", fontSize: 11, color: over ? "var(--fw-red)" : "var(--fw-muted)" }}>
          {row.used} / {row.included} {over ? `· +${row.overage} over (${money(row.overageAmount)})` : ""}
        </span>
      </div>
      <div style={{ height: 6, borderRadius: 3, background: "var(--fw-cream-2)", overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, height: "100%", background: over ? "var(--fw-red)" : "var(--fw-ink)" }} />
      </div>
    </div>
  );
};

const UsageSection = () => {
  const [users, setUsers] = useState([]);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [summary, setSummary] = useState(null);
  const [overage, setOverage] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [allowanceDraft, setAllowanceDraft] = useState({});

  useEffect(() => subscribeAllUsers(setUsers), []);

  const clients = useMemo(() => {
    const q = query.trim().toLowerCase();
    return users
      .filter((u) => !q || (u.displayName || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q))
      .slice(0, 40);
  }, [users, query]);

  const load = async (uid) => {
    setMsg(null); setSummary(null); setOverage(null);
    try {
      const [s, o] = await Promise.all([getClientUsage(uid), getClientOverage(uid)]);
      setSummary(s); setOverage(o);
      setAllowanceDraft(Object.fromEntries(s.actions.map((a) => [a.action, a.included])));
    } catch (e) { setMsg(e?.response?.data?.error || e.message); }
  };

  const select = (u) => { setSelected(u); load(u.id); };

  const genInvoice = async () => {
    if (!selected) return;
    setBusy(true); setMsg(null);
    try {
      const res = await createOverageInvoice(selected.id);
      setMsg(`Invoice created for ${money((res.invoice.amount_due || 0) / 100)} — sent to ${selected.email}.`);
      await load(selected.id);
    } catch (e) { setMsg(e?.response?.data?.error || e.message); }
    finally { setBusy(false); }
  };

  const saveAllowance = async () => {
    if (!selected) return;
    setBusy(true); setMsg(null);
    try {
      await setClientAllowance(selected.id, Object.fromEntries(Object.entries(allowanceDraft).map(([k, v]) => [k, Number(v) || 0])));
      setMsg("Allowance updated."); await load(selected.id);
    } catch (e) { setMsg(e?.response?.data?.error || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="fw-stack-md" style={{ display: "grid", gridTemplateColumns: "300px 1fr", gap: 16, alignItems: "start" }}>
      <Card title="Clients">
        <input className="fw-input" placeholder="Search…" value={query} onChange={(e) => setQuery(e.target.value)} style={{ marginBottom: 10 }} />
        <div style={{ display: "flex", flexDirection: "column" }}>
          {clients.map((u) => (
            <button key={u.id} onClick={() => select(u)} className="fw-btn fw-btn--ghost"
              style={{ justifyContent: "flex-start", textAlign: "left", padding: "10px 8px", borderBottom: "1px solid var(--fw-line)", background: selected?.id === u.id ? "var(--fw-cream-2)" : "transparent" }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{u.displayName || u.email}</div>
                <div style={{ fontFamily: "var(--fw-mono)", fontSize: 10, color: "var(--fw-muted)" }}>{u.email}</div>
              </div>
            </button>
          ))}
          {!clients.length && <Empty>No clients</Empty>}
        </div>
      </Card>

      <Card
        title={selected ? `Usage · ${selected.displayName || selected.email}` : "Select a client"}
        action={selected && overage?.total > 0 ? (
          <button className="fw-btn fw-btn--primary" disabled={busy} onClick={genInvoice}>
            {busy ? "Working…" : `Invoice overage · ${money(overage.total)}`}
          </button>
        ) : null}
      >
        {!selected ? (
          <Empty>Pick a client to see their metered usage and bill any overage.</Empty>
        ) : !summary ? (
          <Empty>Loading…</Empty>
        ) : (
          <>
            <div style={{ fontFamily: "var(--fw-mono)", fontSize: 11, color: "var(--fw-muted)", marginBottom: 6 }}>
              PERIOD {summary.period} · UNBILLED OVERAGE {money(summary.unbilledOverage)}
            </div>
            {summary.actions.map((row) => <Meter key={row.action} row={row} />)}

            <div style={{ marginTop: 20, paddingTop: 16, borderTop: "1px solid var(--fw-line-strong)" }}>
              <div style={{ fontFamily: "var(--fw-mono)", fontSize: 10, letterSpacing: 1.4, textTransform: "uppercase", color: "var(--fw-muted)", marginBottom: 10 }}>
                Included allowance (per month)
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 10 }}>
                {summary.actions.map((a) => (
                  <Field key={a.action} label={a.label}>
                    <input className="fw-input" type="number" min="0" value={allowanceDraft[a.action] ?? a.included}
                      onChange={(e) => setAllowanceDraft((d) => ({ ...d, [a.action]: e.target.value }))} />
                  </Field>
                ))}
              </div>
              <button className="fw-btn fw-btn--ghost" style={{ marginTop: 12 }} disabled={busy} onClick={saveAllowance}>Save allowance</button>
            </div>

            {msg && <div style={{ marginTop: 12, fontSize: 13, color: "var(--fw-ink)" }}>{msg}</div>}
          </>
        )}
      </Card>
    </div>
  );
};

export default UsageSection;
